import axios from 'axios';

// API base URL - exported for use with uploaded assets
// In production (same domain), use empty string for relative URLs
// In development, use localhost
export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? (import.meta.env.PROD ? '' : 'http://127.0.0.1:9001');

// Helper to get full URL for uploaded assets (logos, etc.)
export const getUploadUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('http')) return path;  // Already a full URL
  return `${API_BASE_URL}${path}`;
};

// Create axios instance with base configuration
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Unauthorized - clear token and redirect to login
      localStorage.removeItem('access_token');
      window.location.href = '/';
    }
    return Promise.reject(error);
  }
);

export default api;

// Auth API calls
export const authAPI = {
  login: async (email, password) => {
    // Use URLSearchParams for OAuth2 password flow (application/x-www-form-urlencoded)
    const params = new URLSearchParams();
    params.append('username', email);  // OAuth2 uses 'username' field
    params.append('password', password);
    const response = await api.post('/api/auth/login', params, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    });
    return response.data;
  },

  register: async (email, password, fullName) => {
    const response = await api.post('/api/auth/register', {
      email,
      password,
      full_name: fullName,
    });
    return response.data;
  },

  getCurrentUser: async () => {
    const response = await api.get('/api/auth/me');
    return response.data;
  },

  // Email verification
  verifyEmail: async (token) => {
    const response = await api.get(`/api/auth/verify-email/${token}`);
    return response.data;
  },

  resendVerification: async (email) => {
    const response = await api.post('/api/auth/resend-verification', { email });
    return response.data;
  },

  // Password reset
  forgotPassword: async (email) => {
    const response = await api.post('/api/auth/forgot-password', { email });
    return response.data;
  },

  resetPassword: async (token, newPassword) => {
    const response = await api.post('/api/auth/reset-password', {
      token,
      new_password: newPassword,
    });
    return response.data;
  },
};

// Announcements API calls
export const announcementsAPI = {
  getAnnouncements: async (activeOnly = true, limit = 20) => {
    const response = await api.get('/api/announcements', {
      params: { active_only: activeOnly, limit },
    });
    return response.data;
  },

  getAnnouncement: async (id) => {
    const response = await api.get(`/api/announcements/${id}`);
    return response.data;
  },

  createAnnouncement: async (data) => {
    const response = await api.post('/api/announcements', data);
    return response.data;
  },

  updateAnnouncement: async (id, data) => {
    const response = await api.put(`/api/announcements/${id}`, data);
    return response.data;
  },

  deleteAnnouncement: async (id) => {
    const response = await api.delete(`/api/announcements/${id}`);
    return response.data;
  },

  uploadImage: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/api/announcements/upload-image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  uploadDocument: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/api/announcements/upload-document', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  uploadVideo: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/api/announcements/upload-video', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  previewAsClient: async (domain) => {
    const response = await api.get(`/api/announcements/admin/preview-as/${encodeURIComponent(domain)}`);
    return response.data;
  },
};

// Metrics API calls (admin only)
export const metricsAPI = {
  getSummary: async (excludeInternal = false) => {
    const response = await api.get('/api/admin/metrics/summary', {
      params: { exclude_internal: excludeInternal },
    });
    return response.data;
  },

  getActivityLog: async (params = {}) => {
    const response = await api.get('/api/admin/metrics/activity', { params });
    return response.data;
  },

  getLoginTrend: async (days = 30, excludeInternal = false) => {
    const response = await api.get('/api/admin/metrics/login-trend', {
      params: { days, exclude_internal: excludeInternal },
    });
    return response.data;
  },

  getAppUsage: async (days = 30, excludeInternal = false) => {
    const response = await api.get('/api/admin/metrics/app-usage', {
      params: { days, exclude_internal: excludeInternal },
    });
    return response.data;
  },

  getUserActivity: async (userId, days = 30) => {
    const response = await api.get(`/api/admin/metrics/user/${userId}`, {
      params: { days },
    });
    return response.data;
  },

  cleanupOldLogs: async () => {
    const response = await api.delete('/api/admin/metrics/cleanup');
    return response.data;
  },

  getUsersActivity: async (days = 30) => {
    const response = await api.get('/api/admin/metrics/users', {
      params: { days },
    });
    return response.data;
  },

  exportLogs: async (excludeInternal = true) => {
    const response = await api.get('/api/admin/metrics/export', {
      params: { exclude_internal: excludeInternal },
      responseType: 'blob',
    });
    return response;
  },

  // ── Extended engagement metrics (Overview tab) ──
  getByDomain: async (days = 30) => {
    const response = await api.get('/api/admin/metrics/by-domain', {
      params: { days },
    });
    return response.data;
  },

  getActiveUsers: async (days = 30) => {
    const response = await api.get('/api/admin/metrics/active-users', {
      params: { days },
    });
    return response.data;
  },

  getNewVsReturning: async (weeks = 12) => {
    const response = await api.get('/api/admin/metrics/new-vs-returning', {
      params: { weeks },
    });
    return response.data;
  },

  getDormantUsers: async () => {
    const response = await api.get('/api/admin/metrics/dormant-users');
    return response.data;
  },

  getActivityHeatmap: async (days = 30) => {
    const response = await api.get('/api/admin/metrics/activity-heatmap', {
      params: { days },
    });
    return response.data;
  },

  getPowerUsers: async (days = 30, limit = 10) => {
    const response = await api.get('/api/admin/metrics/power-users', {
      params: { days, limit },
    });
    return response.data;
  },
};

// Activity tracking (for all authenticated users)
export const trackActivity = async (activityType, resource) => {
  try {
    await api.post('/api/auth/track', null, {
      params: { activity_type: activityType, resource },
    });
  } catch (err) {
    // Silently fail - tracking should not block user experience
    console.warn('Failed to track activity:', err);
  }
};

// Admin API calls
// Centrally managed disclaimers (Admin > Client > Disclaimers)
export const disclaimersAPI = {
  // Resolved text for the caller's brand. Errors propagate: callers show them.
  get: async (key, { portfolioCode, brandDomain } = {}) => {
    const params = {};
    if (portfolioCode) params.portfolio_code = portfolioCode;
    if (brandDomain) params.brand_domain = brandDomain;
    const response = await api.get(`/api/disclaimers/${encodeURIComponent(key)}`, { params });
    return response.data;
  },
  listTypes: async (category) => {
    const response = await api.get('/api/disclaimers/types', { params: category ? { category } : {} });
    return response.data;
  },
  // Admin
  getGrid: async () => (await api.get('/api/disclaimers/admin/grid')).data,
  saveCell: async (domain, typeKey, text) =>
    (await api.put('/api/disclaimers/admin/cell', { domain, type_key: typeKey, text })).data,
  createType: async (payload) => (await api.post('/api/disclaimers/admin/types', payload)).data,
  updateType: async (key, payload) =>
    (await api.patch(`/api/disclaimers/admin/types/${encodeURIComponent(key)}`, payload)).data,
  savePlatformNotice: async (platform, text) =>
    (await api.put(`/api/disclaimers/admin/platform-notices/${encodeURIComponent(platform)}`, { text })).data,
  savePlatformPerformance: async (platform, text) =>
    (await api.put(`/api/disclaimers/admin/platform-performance/${encodeURIComponent(platform)}`, { text })).data,
  history: async (params) => (await api.get('/api/disclaimers/admin/history', { params })).data,
  preview: async (typeKey, domain, portfolioCode) =>
    (await api.get('/api/disclaimers/admin/preview', {
      params: { type_key: typeKey, domain, ...(portfolioCode ? { portfolio_code: portfolioCode } : {}) },
    })).data,
};

export const adminAPI = {
  getPermissions: async () => {
    const response = await api.get('/api/admin/permissions');
    return response.data;
  },

  createPermission: async (domain, appName, isAllowed = true) => {
    const response = await api.post('/api/admin/permissions', {
      domain,
      app_name: appName,
      is_allowed: isAllowed,
    });
    return response.data;
  },

  deletePermission: async (permissionId) => {
    const response = await api.delete(`/api/admin/permissions/${permissionId}`);
    return response.data;
  },

  getBrandings: async () => {
    const response = await api.get('/api/admin/brandings');
    return response.data;
  },

  createBranding: async (brandingData) => {
    const response = await api.post('/api/admin/brandings', brandingData);
    return response.data;
  },

  updateBranding: async (domain, brandingData) => {
    const response = await api.put(`/api/admin/brandings/${domain}`, brandingData);
    return response.data;
  },

  previewPermissions: async (domain) => {
    const response = await api.get(`/api/admin/preview-permissions/${encodeURIComponent(domain)}`);
    return response.data;
  },

  getUsers: async () => {
    const response = await api.get('/api/admin/users');
    return response.data;
  },

  registerUser: async (email, password, fullName) => {
    const response = await api.post('/api/admin/register-user', {
      email,
      password,
      full_name: fullName,
    });
    return response.data;
  },

  // Allowed Domains Management
  getAllowedDomains: async () => {
    const response = await api.get('/api/admin/allowed-domains');
    return response.data;
  },

  createAllowedDomain: async (domain, description) => {
    const response = await api.post('/api/admin/allowed-domains', {
      domain,
      description,
    });
    return response.data;
  },

  updateAllowedDomain: async (domainId, data) => {
    const response = await api.put(`/api/admin/allowed-domains/${domainId}`, data);
    return response.data;
  },

  deleteAllowedDomain: async (domainId) => {
    const response = await api.delete(`/api/admin/allowed-domains/${domainId}`);
    return response.data;
  },

  getRegistrationAttempts: async (days = 90) => {
    const response = await api.get('/api/admin/registration-attempts', { params: { days } });
    return response.data;
  },

  // Default Portfolios Management (for white-label clients)
  getDefaultPortfolios: async () => {
    const response = await api.get('/api/admin/default-portfolios');
    return response.data;
  },

  updateDefaultPortfolio: async (portfolioCode, data) => {
    const response = await api.put(`/api/admin/default-portfolios/${portfolioCode}`, data);
    return response.data;
  },

  createDefaultPortfolio: async (data) => {
    const response = await api.post('/api/admin/default-portfolios', data);
    return response.data;
  },

  deleteDefaultPortfolio: async (portfolioCode) => {
    const response = await api.delete(`/api/admin/default-portfolios/${portfolioCode}`);
    return response.data;
  },

  renumberDefaultPortfolios: async () => {
    const response = await api.post('/api/admin/default-portfolios/renumber');
    return response.data;
  },

  getPortfolioWeights: async (code) => {
    const response = await api.get(`/api/admin/default-portfolios/${code}/weights`);
    return response.data;
  },

  savePortfolioWeights: async (code, weights, source = 'manual') => {
    const response = await api.put(`/api/admin/default-portfolios/${code}/weights`, { weights, source });
    return response.data;
  },

  getBenchmarkComparitors: async () => {
    const response = await api.get('/api/admin/benchmark-comparitors');
    return response.data;
  },

  // Domain Portfolio Overrides (custom names per domain)
  getDomainOverrides: async (domain) => {
    const response = await api.get(`/api/admin/domain-portfolio-overrides/${domain}`);
    return response.data;
  },

  setDomainOverrides: async (domain, overrides) => {
    const response = await api.put(`/api/admin/domain-portfolio-overrides/${domain}`, {
      domain,
      overrides,
    });
    return response.data;
  },

  deleteDomainOverrides: async (domain) => {
    const response = await api.delete(`/api/admin/domain-portfolio-overrides/${domain}`);
    return response.data;
  },

  // Domain Portfolio Exclusions (which default portfolios to hide per domain)
  getDomainExclusions: async (domain) => {
    const response = await api.get(`/api/admin/domain-portfolio-exclusions/${domain}`);
    return response.data;
  },

  setDomainExclusions: async (domain, excludedCodes) => {
    const response = await api.put(`/api/admin/domain-portfolio-exclusions/${domain}`, {
      excluded_codes: excludedCodes,
    });
    return response.data;
  },

  deleteDomainExclusions: async (domain) => {
    const response = await api.delete(`/api/admin/domain-portfolio-exclusions/${domain}`);
    return response.data;
  },

  // Bulk Report Generation
  getBulkReportsFunds: async () => {
    const response = await api.get('/api/admin/bulk-reports/funds');
    return response.data;
  },

  // Start background job for report generation - returns job ID immediately
  generateBulkReports: async (apirCodes = null) => {
    const response = await api.post('/api/admin/bulk-reports/generate',
      { apir_codes: apirCodes }
    );
    return response.data;
  },

  // Poll job status
  getBulkReportJobStatus: async (jobId) => {
    const response = await api.get(`/api/admin/bulk-reports/job/${jobId}`);
    return response.data;
  },

  // Download completed job result
  downloadBulkReportJob: async (jobId) => {
    const response = await api.get(`/api/admin/bulk-reports/job/${jobId}/download`, {
      responseType: 'blob',
    });
    return response;
  },

  previewFundReport: async (apirCode) => {
    const response = await api.get(`/api/admin/bulk-reports/preview/${apirCode}`);
    return response.data;
  },

  // Portfolio ICR Management
  getPortfolioICR: async () => {
    const response = await api.get('/api/admin/portfolio-icr');
    return response.data;
  },

  updatePortfolioICR: async (code, data) => {
    const response = await api.put(`/api/admin/portfolio-icr/${code}`, data);
    return response.data;
  },

  bulkUpdatePortfolioICR: async (items) => {
    const response = await api.put('/api/admin/portfolio-icr/bulk', { items });
    return response.data;
  },

  // Capital Market Assumptions (CMA)
  getCMAColumns: async () => {
    const response = await api.get('/api/investment-config/cma/columns');
    return response.data;
  },

  getCMAFunds: async () => {
    const response = await api.get('/api/investment-config/cma/funds');
    return response.data;
  },

  getCMAMappings: async () => {
    const response = await api.get('/api/investment-config/cma/mappings');
    return response.data;
  },

  saveCMAMappings: async (mappings) => {
    const response = await api.put('/api/investment-config/cma/mappings', { mappings });
    return response.data;
  },

  // Taxonomy
  getTaxonomy: async () => {
    const response = await api.get('/api/investment-config/cma/taxonomy');
    return response.data;
  },

  saveTaxonomy: async (data) => {
    const response = await api.put('/api/investment-config/cma/taxonomy', data);
    return response.data;
  },

  // Historic SAA (neutral weights by financial year). Weights are percentages.
  getHistoricSAA: async () => {
    const response = await api.get('/api/investment-config/historic-saa');
    return response.data;
  },

  // Published strategy summary tables (FirstChoice + Strategic Series).
  // financialYear = baseline SAA vintage; thisYearScenarioId drives the main
  // expected return/vol; lastYearScenarioId feeds only the last-year row.
  getStrategySummary: async ({ thisYearScenarioId, lastYearScenarioId, financialYear } = {}) => {
    const params = {};
    if (thisYearScenarioId != null) params.this_year_scenario_id = thisYearScenarioId;
    if (lastYearScenarioId != null) params.last_year_scenario_id = lastYearScenarioId;
    if (financialYear != null) params.financial_year = financialYear;
    const response = await api.get('/api/investment-config/strategy-summary', { params });
    return response.data;
  },

  // body: { financial_year, weights: { blender_code: { sub_asset_class: percent } } }
  saveHistoricSAA: async (data) => {
    const response = await api.put('/api/investment-config/historic-saa', data);
    return response.data;
  },

  deleteHistoricSAAYear: async (financialYear) => {
    const response = await api.delete(`/api/investment-config/historic-saa/${financialYear}`);
    return response.data;
  },

  // Set the portal-wide LIVE SAA vintage; propagates to all portfolios.
  setActiveSAAYear: async (financialYear) => {
    const response = await api.put('/api/investment-config/historic-saa/active-year',
      { financial_year: financialYear });
    return response.data;
  },

  // Portal-wide performance as-of date (null = show latest available).
  getPerformanceAsOfDate: async () => {
    const response = await api.get('/api/investment-config/performance-as-of-date');
    return response.data;
  },

  // Pass null to clear the cap and revert to latest available.
  setPerformanceAsOfDate: async (asOfDate) => {
    const response = await api.put('/api/investment-config/performance-as-of-date',
      { as_of_date: asOfDate });
    return response.data;
  },

  // Monthly performance coverage per shadow portfolio code (missing months, lag, inception).
  getPerformanceCoverage: async () => {
    const response = await api.get('/api/investment-config/performance-coverage');
    return response.data;
  },

  // Portfolio code -> master SAA / client mapping (codes/name/platform from Shadow_Data).
  getPortfolioCodeMapping: async () => {
    const response = await api.get('/api/investment-config/portfolio-code-mapping');
    return response.data;
  },

  // body: { rows: { portfolio_code: { blender_code|null, client_domain|null } } }
  savePortfolioCodeMapping: async (data) => {
    const response = await api.put('/api/investment-config/portfolio-code-mapping', data);
    return response.data;
  },

  getAssetClassOptions: async () => {
    const response = await api.get('/api/investment-config/cma/asset-class-options');
    return response.data;
  },

  // CMA Scenarios
  getCMAScenarios: async () => {
    const response = await api.get('/api/investment-config/cma/scenarios');
    return response.data;
  },

  createCMAScenario: async (data) => {
    const response = await api.post('/api/investment-config/cma/scenarios', data);
    return response.data;
  },

  updateCMAScenario: async (id, data) => {
    const response = await api.put(`/api/investment-config/cma/scenarios/${id}`, data);
    return response.data;
  },

  deleteCMAScenario: async (id) => {
    const response = await api.delete(`/api/investment-config/cma/scenarios/${id}`);
    return response.data;
  },

  setCMAScenarioDefault: async (id) => {
    const response = await api.put(`/api/investment-config/cma/scenarios/${id}/default`);
    return response.data;
  },

  fillMissingCMAScenarioRows: async (id) => {
    const response = await api.post(`/api/investment-config/cma/scenarios/${id}/fill-missing-rows`);
    return response.data;
  },

  getScenarioReturns: async (id) => {
    const response = await api.get(`/api/investment-config/cma/scenarios/${id}/returns`);
    return response.data;
  },

  saveScenarioReturns: async (id, returns) => {
    const response = await api.put(`/api/investment-config/cma/scenarios/${id}/returns`, returns);
    return response.data;
  },

  getScenarioCovariance: async (id) => {
    const response = await api.get(`/api/investment-config/cma/scenarios/${id}/covariance`);
    return response.data;
  },

  saveScenarioCovariance: async (id, covariance) => {
    const response = await api.put(`/api/investment-config/cma/scenarios/${id}/covariance`, covariance);
    return response.data;
  },

  lockScenario: async (id, reason = null) => {
    const response = await api.post(`/api/investment-config/cma/scenarios/${id}/lock`, { reason });
    return response.data;
  },

  unlockScenario: async (id, reason) => {
    const response = await api.post(`/api/investment-config/cma/scenarios/${id}/unlock`, { reason });
    return response.data;
  },

  getScenarioLockHistory: async (id) => {
    const response = await api.get(`/api/investment-config/cma/scenarios/${id}/lock-history`);
    return response.data;
  },

  // Sharing: access = 'edit' (any admin) | 'read' (owner-edits-only). Owner only.
  setCMAScenarioSharing: async (id, access) => {
    const response = await api.put(`/api/investment-config/cma/scenarios/${id}/sharing`, { access });
    return response.data;
  },

  downloadCovarianceMatrixTemplate: async (id) => {
    const response = await api.get(`/api/investment-config/cma/scenarios/${id}/covariance/matrix-template.xlsx`, {
      responseType: 'blob',
    });
    return response;
  },

  uploadCovarianceMatrix: async (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`/api/investment-config/cma/scenarios/${id}/covariance/upload-matrix`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  importBlenderCMA: async () => {
    const response = await api.post('/api/investment-config/cma/scenarios/import-blender');
    return response.data;
  },

  downloadCMAScenarioTemplate: async () => {
    const response = await api.get('/api/investment-config/cma/scenarios/template.xlsx', {
      responseType: 'blob',
    });
    return response;
  },

  exportCMAScenario: async (id) => {
    const response = await api.get(`/api/investment-config/cma/scenarios/${id}/export.xlsx`, {
      responseType: 'blob',
    });
    return response;
  },

  uploadCMAScenarioExcel: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/api/investment-config/cma/scenarios/import-excel', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  // Custom Data Columns
  getCustomData: async () => {
    const response = await api.get('/api/investment-config/custom-data');
    return response.data;
  },

  createCustomData: async (data) => {
    const response = await api.post('/api/investment-config/custom-data', data);
    return response.data;
  },

  updateCustomData: async (id, data) => {
    const response = await api.put(`/api/investment-config/custom-data/${id}`, data);
    return response.data;
  },

  deleteCustomData: async (id) => {
    const response = await api.delete(`/api/investment-config/custom-data/${id}`);
    return response.data;
  },

  previewCustomData: async (data) => {
    const response = await api.post('/api/investment-config/custom-data/preview', data);
    return response.data;
  },

  // Stress Events Management
  getStressEvents: async () => {
    const response = await api.get('/api/investment-config/stress-events');
    return response.data;
  },

  createStressEvent: async (data) => {
    const response = await api.post('/api/investment-config/stress-events', data);
    return response.data;
  },

  updateStressEvent: async (id, data) => {
    const response = await api.put(`/api/investment-config/stress-events/${id}`, data);
    return response.data;
  },

  deleteStressEvent: async (id) => {
    const response = await api.delete(`/api/investment-config/stress-events/${id}`);
    return response.data;
  },

  seedStressEvents: async () => {
    const response = await api.post('/api/investment-config/stress-events/seed');
    return response.data;
  },

  // Macro Regimes Management
  getMacroRegimes: async () => {
    const response = await api.get('/api/investment-config/macro-regimes');
    return response.data;
  },

  createMacroRegime: async (data) => {
    const response = await api.post('/api/investment-config/macro-regimes', data);
    return response.data;
  },

  updateMacroRegime: async (id, data) => {
    const response = await api.put(`/api/investment-config/macro-regimes/${id}`, data);
    return response.data;
  },

  deleteMacroRegime: async (id) => {
    const response = await api.delete(`/api/investment-config/macro-regimes/${id}`);
    return response.data;
  },

  seedMacroRegimes: async () => {
    const response = await api.post('/api/investment-config/macro-regimes/seed');
    return response.data;
  },

  // SAC data-history start per asset class (Min(date) in monthly_historic_returns)
  getSacHistoryStart: async () => {
    const response = await api.get('/api/investment-config/cma/sac-history-start');
    return response.data;
  },

  // CMA Backfill Proxies (Stambaugh)
  getCMABackfillProxies: async () => {
    const response = await api.get('/api/investment-config/cma/backfill-proxies');
    return response.data;
  },

  saveCMABackfillProxies: async (proxies) => {
    const response = await api.put('/api/investment-config/cma/backfill-proxies', { proxies });
    return response.data;
  },

  // CMA historical covariance compute
  previewScenarioCovariance: async (scenarioId, body) => {
    const response = await api.post(`/api/investment-config/cma/scenarios/${scenarioId}/covariance/preview`, body);
    return response.data;
  },

  computeAndSaveScenarioCovariance: async (scenarioId, body) => {
    const response = await api.post(`/api/investment-config/cma/scenarios/${scenarioId}/covariance/compute-and-save`, body);
    return response.data;
  },

  getScenarioCovarianceDiagnostics: async (scenarioId) => {
    const response = await api.get(`/api/investment-config/cma/scenarios/${scenarioId}/covariance/diagnostics`);
    return response.data;
  },

  // Saved optimisation runs
  listSavedOptimiserRuns: async ({ favouritesOnly = false, mineOnly = false } = {}) => {
    const params = new URLSearchParams();
    if (favouritesOnly) params.set('favourites_only', 'true');
    if (mineOnly) params.set('mine_only', 'true');
    const q = params.toString() ? `?${params}` : '';
    const response = await api.get(`/api/optimiser/saved-runs${q}`);
    return response.data;
  },

  getSavedOptimiserRun: async (id) => {
    const response = await api.get(`/api/optimiser/saved-runs/${id}`);
    return response.data;
  },

  createSavedOptimiserRun: async (data) => {
    const response = await api.post('/api/optimiser/saved-runs', data);
    return response.data;
  },

  updateSavedOptimiserRun: async (id, data) => {
    const response = await api.put(`/api/optimiser/saved-runs/${id}`, data);
    return response.data;
  },

  deleteSavedOptimiserRun: async (id) => {
    const response = await api.delete(`/api/optimiser/saved-runs/${id}`);
    return response.data;
  },

  // Sharing: access = 'edit' (any user) | 'read' (owner-edits-only). Owner only.
  setSavedOptimiserRunSharing: async (id, access) => {
    const response = await api.put(`/api/optimiser/saved-runs/${id}/sharing`, { access });
    return response.data;
  },

  // Permanently lock a run (frozen for everyone, no unlock). Owner only.
  lockSavedOptimiserRun: async (id) => {
    const response = await api.post(`/api/optimiser/saved-runs/${id}/lock`);
    return response.data;
  },

  // Weighted blend of saved batch optimisations. `runs` is an array of
  // { run_id, weight }. Returns { results: { profile: OptimiseResult }, blend_source: [...] }.
  blendOptimiserBatches: async ({ runs, scenario_id = null } = {}) => {
    const body = { runs };
    if (scenario_id) body.scenario_id = scenario_id;
    const response = await api.post('/api/optimiser/blend-batches', body);
    return response.data;
  },

  // CMA data (asset classes + correlation matrix) for a given scenario. Pass
  // scenarioId=null to use the active default scenario. Used by the Batch tab's
  // multi-run Excel export to pull each run's own scenario CMAs/correlation.
  getOptimiserData: async (scenarioId = null) => {
    const q = scenarioId != null ? `?scenario_id=${encodeURIComponent(scenarioId)}` : '';
    const response = await api.get(`/api/optimiser/data${q}`);
    return response.data;
  },

  // Saved optimiser configs (batch templates + asset constraint sets)
  listOptimiserConfigs: async (kind) => {
    const response = await api.get(`/api/optimiser/saved-configs?kind=${encodeURIComponent(kind)}`);
    return response.data;
  },

  createOptimiserConfig: async (data) => {
    const response = await api.post('/api/optimiser/saved-configs', data);
    return response.data;
  },

  updateOptimiserConfig: async (id, data) => {
    const response = await api.put(`/api/optimiser/saved-configs/${id}`, data);
    return response.data;
  },

  deleteOptimiserConfig: async (id) => {
    const response = await api.delete(`/api/optimiser/saved-configs/${id}`);
    return response.data;
  },

  // Private Market Betas Management
  getPrivateMarketBetas: async () => {
    const response = await api.get('/api/investment-config/private-market-betas');
    return response.data;
  },

  createPrivateMarketBeta: async (data) => {
    const response = await api.post('/api/investment-config/private-market-betas', data);
    return response.data;
  },

  updatePrivateMarketBeta: async (id, data) => {
    const response = await api.put(`/api/investment-config/private-market-betas/${id}`, data);
    return response.data;
  },

  deletePrivateMarketBeta: async (id) => {
    const response = await api.delete(`/api/investment-config/private-market-betas/${id}`);
    return response.data;
  },

  seedPrivateMarketBetas: async () => {
    const response = await api.post('/api/investment-config/private-market-betas/seed');
    return response.data;
  },
};

// Allocation Calculator API calls
export const allocationCalculatorAPI = {
  getVehicles: async () => {
    const response = await api.get('/api/allocation-calculator/vehicles');
    return response.data;
  },

  getHoldings: async (codes) => {
    const response = await api.get('/api/allocation-calculator/holdings', {
      params: { portfolio_codes: codes.join(',') },
    });
    return response.data;
  },

  getFunds: async () => {
    const response = await api.get('/api/allocation-calculator/funds');
    return response.data;
  },

  // Saved Portfolio Lab portfolios — Drummond staff only (403 otherwise).
  getLabPortfolios: async () => {
    const response = await api.get('/api/allocation-calculator/lab-portfolios');
    return response.data;
  },

  getLabHoldings: async (ids) => {
    const response = await api.get('/api/allocation-calculator/lab-portfolios/holdings', {
      params: { ids: ids.join(',') },
    });
    return response.data;
  },

  exportPdf: async (data) => {
    const response = await api.post('/api/allocation-calculator/export-pdf', data, {
      responseType: 'blob',
    });
    return response.data;
  },
};

// Portfolio Registry API (authenticated, not admin-only)
export const portfolioRegistryAPI = {
  // Pass an app key (e.g. "portfolio_lab") to filter to portfolios visible in that app.
  getRegistry: async (app = null) => {
    const params = app ? { app } : undefined;
    const response = await api.get('/api/investment-config/portfolios/registry', { params });
    return response.data;
  },
};

// Resources API calls
export const resourcesAPI = {
  // Public endpoints (all authenticated users)
  getCategories: async () => {
    const response = await api.get('/api/resources/categories');
    return response.data;
  },

  getCategoryItems: async (categoryId) => {
    const response = await api.get(`/api/resources/categories/${categoryId}/items`);
    return response.data;
  },

  searchItems: async (query) => {
    const response = await api.get(`/api/resources/search?q=${encodeURIComponent(query)}`);
    return response.data;
  },

  getDownloadUrl: (itemId) => {
    const token = localStorage.getItem('access_token');
    return `${API_BASE_URL}/api/resources/download/${itemId}?token=${token}`;
  },

  downloadFolder: async (folderId) => {
    const response = await api.get(`/api/resources/download-folder/${folderId}`, {
      responseType: 'blob',
    });
    return response;
  },

  // Admin endpoints (Drummond only)
  adminGetCategories: async () => {
    const response = await api.get('/api/resources/admin/categories');
    return response.data;
  },

  previewAsClient: async (domain) => {
    const response = await api.get(`/api/resources/admin/preview-as/${encodeURIComponent(domain)}`);
    return response.data;
  },

  createCategory: async (data) => {
    const response = await api.post('/api/resources/admin/categories', data);
    return response.data;
  },

  updateCategory: async (categoryId, data) => {
    const response = await api.put(`/api/resources/admin/categories/${categoryId}`, data);
    return response.data;
  },

  deleteCategory: async (categoryId) => {
    const response = await api.delete(`/api/resources/admin/categories/${categoryId}`);
    return response.data;
  },

  // Folder endpoints
  createFolder: async (data) => {
    const response = await api.post('/api/resources/admin/folders', data);
    return response.data;
  },

  updateFolder: async (folderId, data) => {
    const response = await api.put(`/api/resources/admin/folders/${folderId}`, data);
    return response.data;
  },

  deleteFolder: async (folderId) => {
    const response = await api.delete(`/api/resources/admin/folders/${folderId}`);
    return response.data;
  },

  createItem: async (formData) => {
    const response = await api.post('/api/resources/admin/items', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  bulkUploadItems: async (formData, onUploadProgress) => {
    const response = await api.post('/api/resources/admin/items/bulk', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress,
    });
    return response.data;
  },

  updateItem: async (itemId, data) => {
    const response = await api.put(`/api/resources/admin/items/${itemId}`, data);
    return response.data;
  },

  deleteItem: async (itemId) => {
    const response = await api.delete(`/api/resources/admin/items/${itemId}`);
    return response.data;
  },

  bulkDeleteItems: async (itemIds) => {
    const response = await api.post('/api/resources/admin/items/bulk-delete', { item_ids: itemIds });
    return response.data;
  },

  bulkMoveItems: async (itemIds, categoryId, folderId = null) => {
    const response = await api.post('/api/resources/admin/items/bulk-move', {
      item_ids: itemIds,
      category_id: categoryId,
      folder_id: folderId,
    });
    return response.data;
  },

  reorderCategories: async (items) => {
    const response = await api.post('/api/resources/admin/categories/reorder', { items });
    return response.data;
  },

  reorderItems: async (items) => {
    const response = await api.post('/api/resources/admin/items/reorder', { items });
    return response.data;
  },
  reorderFolders: async (items) => {
    const response = await api.post('/api/resources/admin/folders/reorder', { items });
    return response.data;
  },

  seedCategories: async () => {
    const response = await api.post('/api/resources/admin/seed-categories');
    return response.data;
  },

  getCpdEligible: async () => {
    const response = await api.get('/api/resources/cpd-eligible');
    return response.data;
  },
};

// FAQ API calls (public - for authenticated users)
export const faqAPI = {
  getAll: async (category = null) => {
    const params = category ? { category } : {};
    const response = await api.get('/api/faq', { params });
    return response.data;
  },

  search: async (query) => {
    const response = await api.get('/api/faq/search', { params: { q: query } });
    return response.data;
  },

  getCategories: async () => {
    const response = await api.get('/api/faq/categories');
    return response.data;
  },
};

// FAQ Admin API calls (Drummond only)
export const faqAdminAPI = {
  getAll: async () => {
    const response = await api.get('/api/admin/faqs');
    return response.data;
  },

  create: async (data) => {
    const response = await api.post('/api/admin/faqs', data);
    return response.data;
  },

  update: async (id, data) => {
    const response = await api.put(`/api/admin/faqs/${id}`, data);
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`/api/admin/faqs/${id}`);
    return response.data;
  },

  // Category management
  getCategories: async () => {
    const response = await api.get('/api/admin/faq-categories');
    return response.data;
  },

  createCategory: async (data) => {
    const response = await api.post('/api/admin/faq-categories', data);
    return response.data;
  },

  updateCategory: async (id, data) => {
    const response = await api.put(`/api/admin/faq-categories/${id}`, data);
    return response.data;
  },

  deleteCategory: async (id) => {
    const response = await api.delete(`/api/admin/faq-categories/${id}`);
    return response.data;
  },
};


// Email Lists API calls
export const emailListsAPI = {
  getLists: async () => {
    const response = await api.get('/api/email-lists');
    return response.data;
  },

  getList: async (id) => {
    const response = await api.get(`/api/email-lists/${id}`);
    return response.data;
  },

  createList: async (data) => {
    const response = await api.post('/api/email-lists', data);
    return response.data;
  },

  updateList: async (id, data) => {
    const response = await api.put(`/api/email-lists/${id}`, data);
    return response.data;
  },

  deleteList: async (id) => {
    const response = await api.delete(`/api/email-lists/${id}`);
    return response.data;
  },

  getMembers: async (id) => {
    const response = await api.get(`/api/email-lists/${id}/members`);
    return response.data;
  },
};

// Research Reports Bulk Download API calls
export const researchReportsBulkAPI = {
  getFunds: async () => {
    const response = await api.get('/api/reports/bulk-download/funds');
    return response.data;
  },
  generate: async (apirCodes) => {
    const response = await api.post('/api/reports/bulk-download/generate', { apir_codes: apirCodes });
    return response.data;
  },
  jobStatus: async (jobId) => {
    const response = await api.get(`/api/reports/bulk-download/job/${jobId}`);
    return response.data;
  },
  download: async (jobId) => {
    return await api.get(`/api/reports/bulk-download/job/${jobId}/download`, { responseType: 'blob' });
  },
  getPortfolios: async () => {
    const response = await api.get('/api/reports/bulk-download/portfolios');
    return response.data;
  },
};

// Factsheet Bulk Download API calls
export const factsheetBulkAPI = {
  generate: async (items) => {
    const response = await api.post('/api/factsheets/bulk-pdf/generate', { items });
    return response.data;
  },

  jobStatus: async (jobId) => {
    const response = await api.get(`/api/factsheets/bulk-pdf/job/${jobId}`);
    return response.data;
  },

  download: async (jobId) => {
    const response = await api.get(`/api/factsheets/bulk-pdf/job/${jobId}/download`, {
      responseType: 'blob',
    });
    return response;
  },
};

// Portfolio Reports API calls
export const investmentPerspectivesAPI = {
  getAvailableMonths: async () => {
    const response = await api.get('/api/investment-perspectives/available-months');
    return response.data;
  },
  getReportData: async (period, brandDomain = null) => {
    const params = brandDomain ? { brand_domain: brandDomain } : {};
    const response = await api.get(`/api/investment-perspectives/report/${period}`, { params });
    return response.data;
  },
  getContentOnly: async (period, brandDomain = null) => {
    const params = brandDomain ? { brand_domain: brandDomain } : {};
    const response = await api.get(`/api/investment-perspectives/report/${period}/content`, { params });
    return response.data;
  },
  getContent: async (period, domain = '_default') => {
    const response = await api.get(`/api/investment-perspectives/admin/content/${period}`, { params: { domain } });
    return response.data;
  },
  saveContent: async (period, data, domain = '_default') => {
    const response = await api.put(`/api/investment-perspectives/admin/content/${period}`, data, { params: { domain } });
    return response.data;
  },
  deleteContent: async (period, domain = '_default') => {
    const response = await api.delete(`/api/investment-perspectives/admin/content/${period}`, { params: { domain } });
    return response.data;
  },
  listPeriods: async () => {
    const response = await api.get('/api/investment-perspectives/admin/content/periods');
    return response.data;
  },
  getPeriodDates: async (period) => {
    const response = await api.get(`/api/investment-perspectives/period-dates/${period}`);
    return response.data;
  },
  getPublishedPeriods: async () => {
    const response = await api.get('/api/investment-perspectives/published-periods');
    return response.data;
  },
  savePeriodDates: async (period, perfDate) => {
    const response = await api.put(`/api/investment-perspectives/period-dates/${period}`, { perf_date: perfDate || null });
    return response.data;
  },
  // domains optional: omitted = everyone; ['_default'] = non-tailored clients;
  // other entries are client domains (e.g. tailored brands)
  publishPeriod: async (period, domains = null) => {
    const response = await api.post(`/api/investment-perspectives/period-dates/${period}/publish`,
      domains && domains.length ? { domains } : {});
    return response.data;
  },
  unpublishPeriod: async (period, domains = null) => {
    const response = await api.post(`/api/investment-perspectives/period-dates/${period}/unpublish`,
      domains && domains.length ? { domains } : {});
    return response.data;
  },
  getBrands: async () => {
    const response = await api.get('/api/investment-perspectives/brands');
    return response.data;
  },
  getWhitelabelDomains: async (period) => {
    const response = await api.get(`/api/investment-perspectives/whitelabel-domains/${period}`);
    return response.data;
  },
  updateWhitelabelDomains: async (period, domains, enabled) => {
    const response = await api.post(`/api/investment-perspectives/whitelabel-domains/${period}`, { domains, enabled });
    return response.data;
  },
  copyWhitelabelDomains: async (period, sourcePeriod) => {
    const response = await api.post(`/api/investment-perspectives/whitelabel-domains/${period}/copy-from/${sourcePeriod}`);
    return response.data;
  },
  getPositioningSuites: async () => {
    const response = await api.get('/api/investment-perspectives/positioning-suites');
    return response.data;
  },
  setPositioningSuites: async (domain, suites) => {
    const response = await api.put(`/api/investment-perspectives/positioning-suites/${encodeURIComponent(domain)}`, { suites });
    return response.data;
  },
  listVersions: async (period, domain = '_default') => {
    const response = await api.get(`/api/investment-perspectives/admin/content/${period}/versions`, { params: { domain } });
    return response.data;
  },
  getVersion: async (versionId) => {
    const response = await api.get(`/api/investment-perspectives/admin/content/version/${versionId}`);
    return response.data;
  },
  restoreVersion: async (versionId) => {
    const response = await api.post(`/api/investment-perspectives/admin/content/version/${versionId}/restore`);
    return response.data;
  },
  propagate: async (payload) => {
    const response = await api.post('/api/investment-perspectives/admin/content/propagate', payload);
    return response.data;
  },
  copyFrom: async (period, sourcePeriod, domain = '_default') => {
    const response = await api.post(`/api/investment-perspectives/admin/content/${period}/copy-from/${sourcePeriod}`, null, { params: { domain } });
    return response.data;
  },
  downloadPdf: async (period, htmlContent, brandDomain = null) => {
    const params = brandDomain ? { brand_domain: brandDomain } : {};
    return api.post(
      `/api/investment-perspectives/report/${period}/pdf`,
      { html_content: htmlContent },
      { params, responseType: 'blob' }
    );
  },
  // Editable text-only RTF (Word) of the report commentary. Drummond staff only.
  downloadRtf: async (period, brandDomain = null) => {
    const params = brandDomain ? { brand_domain: brandDomain } : {};
    return api.get(
      `/api/investment-perspectives/report/${period}/rtf`,
      { params, responseType: 'blob' }
    );
  },
  // Preview an uploaded edited RTF (no save). Returns { changes, target_warning, ... }.
  parseRtf: async (period, file, brandDomain = null) => {
    const params = brandDomain ? { brand_domain: brandDomain } : {};
    const form = new FormData();
    form.append('file', file);
    const res = await api.post(
      `/api/investment-perspectives/report/${period}/rtf/parse`,
      form, { params, headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return res.data;
  },
  // Apply an uploaded edited RTF to the report commentary (saves, versioned).
  applyRtf: async (period, file, brandDomain = null) => {
    const params = brandDomain ? { brand_domain: brandDomain } : {};
    const form = new FormData();
    form.append('file', file);
    const res = await api.post(
      `/api/investment-perspectives/report/${period}/rtf/apply`,
      form, { params, headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return res.data;
  },
  bulkWysiwyg: async (items) => {
    return api.post('/api/investment-perspectives/bulk-pdf/wysiwyg', { items }, { responseType: 'blob' });
  },
};

export const portfolioReportsAPI = {
  getPortfolios: async (quarter) => {
    const params = quarter ? { quarter } : {};
    const response = await api.get('/api/portfolio-reports/portfolios', { params });
    return response.data;
  },
  getBrands: async () => {
    const response = await api.get('/api/portfolio-reports/portfolios/brands');
    return response.data;
  },
  getBrandPortfolios: async (domain) => {
    const response = await api.get(`/api/portfolio-reports/portfolios/brands/${domain}`);
    return response.data;
  },
  getAvailableQuarters: async (frequency = 'quarterly') => {
    const response = await api.get('/api/portfolio-reports/available-quarters', {
      params: { frequency },
    });
    return response.data;
  },
  publishQuarter: async (quarter) => {
    const response = await api.post(`/api/portfolio-reports/quarter-dates/${quarter}/publish`);
    return response.data;
  },
  unpublishQuarter: async (quarter) => {
    const response = await api.post(`/api/portfolio-reports/quarter-dates/${quarter}/unpublish`);
    return response.data;
  },
  getPublishedPortfolios: async (quarter) => {
    const response = await api.get(`/api/portfolio-reports/quarter-dates/${quarter}/published-portfolios`);
    return response.data;
  },
  publishPortfolio: async (quarter, portfolioCode, published) => {
    const response = await api.post(`/api/portfolio-reports/quarter-dates/${quarter}/publish-portfolio`, {
      portfolio_code: portfolioCode,
      published,
    });
    return response.data;
  },
  publishPortfoliosBulk: async (quarter, portfolioCodes) => {
    const response = await api.post(`/api/portfolio-reports/quarter-dates/${quarter}/publish-portfolios-bulk`, {
      portfolio_codes: portfolioCodes,
    });
    return response.data;
  },
  getPublishEvents: async (quarter, limit = 200) => {
    const response = await api.get('/api/portfolio-reports/publish-events', {
      params: { quarter, limit },
    });
    return response.data;
  },
  getAvailableDates: async (portfolioCode) => {
    const response = await api.get(`/api/portfolio-reports/portfolio/${portfolioCode}/available-dates`);
    return response.data;
  },
  getQuarterDates: async (quarter) => {
    const response = await api.get(`/api/portfolio-reports/quarter-dates/${quarter}`);
    return response.data;
  },
  saveQuarterDates: async (quarter, { perfDate, holdingsDate, changesBaseDate, saaYear }) => {
    const response = await api.put(`/api/portfolio-reports/quarter-dates/${quarter}`, {
      perf_date: perfDate || null,
      holdings_date: holdingsDate || null,
      changes_base_date: changesBaseDate || null,
      saa_year: saaYear || null,
    });
    return response.data;
  },
  getQuarterDateOverrides: async (quarter) => {
    const response = await api.get(`/api/portfolio-reports/quarter-dates/${quarter}/overrides`);
    return response.data;
  },
  savePortfolioDateOverride: async (quarter, portfolioCode, { perfDate, holdingsDate, changesBaseDate } = {}) => {
    const response = await api.put(`/api/portfolio-reports/quarter-dates/${quarter}/overrides/${portfolioCode}`, {
      perf_date: perfDate || null,
      holdings_date: holdingsDate || null,
      changes_base_date: changesBaseDate || null,
    });
    return response.data;
  },
  getReportData: async (portfolioCode, quarter, brandDomain = null, { perfDate, holdingsDate, changesBaseDate, saaYear } = {}) => {
    const params = new URLSearchParams();
    if (quarter) params.append('quarter', quarter);
    if (brandDomain) params.append('brand_domain', brandDomain);
    if (perfDate) params.append('perf_date', perfDate);
    if (holdingsDate) params.append('holdings_date', holdingsDate);
    if (changesBaseDate) params.append('changes_base_date', changesBaseDate);
    if (saaYear) params.append('saa_year', saaYear);
    const response = await api.get(`/api/portfolio-reports/portfolio/${portfolioCode}?${params}`);
    return response.data;
  },
  getContentOnly: async (portfolioCode, quarter, brandDomain = null) => {
    const params = new URLSearchParams();
    if (quarter) params.append('quarter', quarter);
    if (brandDomain) params.append('brand_domain', brandDomain);
    const response = await api.get(`/api/portfolio-reports/portfolio/${portfolioCode}/content?${params}`);
    return response.data;
  },
  getContent: async (portfolioCode, quarter, domain = '_default') => {
    const response = await api.get(`/api/portfolio-reports/admin/content/${portfolioCode}/${quarter}?domain=${domain}`);
    return response.data;
  },
  saveContent: async (portfolioCode, quarter, content, domain = '_default') => {
    const response = await api.put(`/api/portfolio-reports/admin/content/${portfolioCode}/${quarter}?domain=${domain}`, content);
    return response.data;
  },
  deleteContent: async (portfolioCode, quarter, domain = '_default') => {
    const response = await api.delete(`/api/portfolio-reports/admin/content/${portfolioCode}/${quarter}?domain=${domain}`);
    return response.data;
  },
  listBrandCopies: async (quarter) => {
    const response = await api.get('/api/portfolio-reports/admin/content/brand-copies', { params: { quarter } });
    return response.data;
  },
  listQuarters: async (portfolioCode) => {
    const response = await api.get(`/api/portfolio-reports/admin/content/quarters/${portfolioCode}`);
    return response.data;
  },
  propagateContent: async ({ sourcePortfolio, quarter, targetQuarter, domain, targetDomain, targetPortfolios, sections }) => {
    const response = await api.post('/api/portfolio-reports/admin/content/propagate', {
      source_portfolio: sourcePortfolio,
      quarter,
      target_quarter: targetQuarter || null,
      domain: domain || '_default',
      target_domain: targetDomain || null,
      target_portfolios: targetPortfolios,
      sections,
    });
    return response.data;
  },
  copyContent: async (portfolioCode, targetQuarter, sourceQuarter, domain = '_default') => {
    const response = await api.post(`/api/portfolio-reports/admin/content/${portfolioCode}/${targetQuarter}/copy-from/${sourceQuarter}?domain=${domain}`);
    return response.data;
  },
  listVersions: async (portfolioCode, quarter, domain = '_default') => {
    const response = await api.get(`/api/portfolio-reports/admin/content/${portfolioCode}/${quarter}/versions?domain=${domain}`);
    return response.data;
  },
  getVersion: async (versionId) => {
    const response = await api.get(`/api/portfolio-reports/admin/content/version/${versionId}`);
    return response.data;
  },
  restoreVersion: async (versionId) => {
    const response = await api.post(`/api/portfolio-reports/admin/content/version/${versionId}/restore`);
    return response.data;
  },
  downloadPdf: async (portfolioCode, quarter, chartImages = null, brandDomain = null, { perfDate, holdingsDate, changesBaseDate, saaYear } = {}, htmlContent = null) => {
    const params = new URLSearchParams();
    if (quarter) params.append('quarter', quarter);
    if (brandDomain) params.append('brand_domain', brandDomain);
    if (perfDate) params.append('perf_date', perfDate);
    if (holdingsDate) params.append('holdings_date', holdingsDate);
    if (changesBaseDate) params.append('changes_base_date', changesBaseDate);
    if (saaYear) params.append('saa_year', saaYear);
    const response = await api.post(
      `/api/portfolio-reports/portfolio/${portfolioCode}/pdf?${params}`,
      { chart_images: chartImages, html_content: htmlContent },
      { responseType: 'blob' }
    );
    return response;
  },
  // Editable text-only RTF (Word) of the report commentary. Drummond staff only.
  downloadRtf: async (portfolioCode, quarter, brandDomain = null) => {
    const params = {};
    if (quarter) params.quarter = quarter;
    if (brandDomain) params.brand_domain = brandDomain;
    return api.get(
      `/api/portfolio-reports/portfolio/${portfolioCode}/rtf`,
      { params, responseType: 'blob' }
    );
  },
  // Preview an uploaded edited RTF (no save). Returns { changes, target_warning, ... }.
  parseRtf: async (portfolioCode, quarter, file, brandDomain = null) => {
    const params = {};
    if (quarter) params.quarter = quarter;
    if (brandDomain) params.brand_domain = brandDomain;
    const form = new FormData();
    form.append('file', file);
    const res = await api.post(
      `/api/portfolio-reports/portfolio/${portfolioCode}/rtf/parse`,
      form, { params, headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return res.data;
  },
  // Apply an uploaded edited RTF to the report commentary (saves, versioned).
  applyRtf: async (portfolioCode, quarter, file, brandDomain = null) => {
    const params = {};
    if (quarter) params.quarter = quarter;
    if (brandDomain) params.brand_domain = brandDomain;
    const form = new FormData();
    form.append('file', file);
    const res = await api.post(
      `/api/portfolio-reports/portfolio/${portfolioCode}/rtf/apply`,
      form, { params, headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return res.data;
  },
};

export const portfolioReportsBulkAPI = {
  generate: async (items, quarter, { perfDate, holdingsDate, changesBaseDate, saaYear } = {}) => {
    const response = await api.post('/api/portfolio-reports/bulk-pdf/generate', {
      items,
      quarter,
      perf_date: perfDate || null,
      holdings_date: holdingsDate || null,
      changes_base_date: changesBaseDate || null,
      saa_year: saaYear || null,
    });
    return response.data;
  },

  jobStatus: async (jobId) => {
    const response = await api.get(`/api/portfolio-reports/bulk-pdf/job/${jobId}`);
    return response.data;
  },

  download: async (jobId, quarter) => {
    const params = quarter ? `?quarter=${encodeURIComponent(quarter)}` : '';
    const response = await api.get(
      `/api/portfolio-reports/bulk-pdf/job/${jobId}/download${params}`,
      { responseType: 'blob' }
    );
    return response;
  },

  generateWysiwyg: async (items) => {
    const response = await api.post(
      '/api/portfolio-reports/bulk-pdf/wysiwyg',
      { items },
      { responseType: 'blob', timeout: 600000 }
    );
    return response;
  },
};

// Retirement Scenarios API calls
export const retirementScenariosAPI = {
  saveScenario: async (clientIdentifier, scenarioName, formInputs, resultsData) => {
    const response = await api.post('/api/retirement/scenarios', {
      client_identifier: clientIdentifier,
      scenario_name: scenarioName,
      form_inputs: formInputs,
      results_data: resultsData,
    });
    return response.data;
  },

  listScenarios: async (clientIdentifier = null, limit = 50, offset = 0) => {
    const params = { limit, offset };
    if (clientIdentifier) {
      params.client_identifier = clientIdentifier;
    }
    const response = await api.get('/api/retirement/scenarios', { params });
    return response.data;
  },

  listClients: async (search = null) => {
    const params = {};
    if (search) {
      params.search = search;
    }
    const response = await api.get('/api/retirement/scenarios/clients', { params });
    return response.data;
  },

  getScenario: async (id) => {
    const response = await api.get(`/api/retirement/scenarios/${id}`);
    return response.data;
  },

  getClientTimeline: async (clientIdentifier) => {
    const response = await api.get(`/api/retirement/scenarios/client/${encodeURIComponent(clientIdentifier)}`);
    return response.data;
  },

  getClientScenariosDetails: async (clientIdentifier) => {
    const response = await api.get(`/api/retirement/scenarios/client/${encodeURIComponent(clientIdentifier)}/details`);
    return response.data;
  },

  updateScenario: async (id, data) => {
    const response = await api.put(`/api/retirement/scenarios/${id}`, data);
    return response.data;
  },

  deleteScenario: async (id) => {
    const response = await api.delete(`/api/retirement/scenarios/${id}`);
    return response.data;
  },

  deleteClient: async (clientIdentifier) => {
    const response = await api.delete(`/api/retirement/scenarios/client/${encodeURIComponent(clientIdentifier)}`);
    return response.data;
  },
};

// CPD API calls (user-facing)
export const cpdAPI = {
  getTasks: async () => {
    const response = await api.get('/api/cpd/tasks');
    return response.data;
  },

  getTask: async (taskId) => {
    const response = await api.get(`/api/cpd/tasks/${taskId}`);
    return response.data;
  },

  getTaskQuestions: async (taskId) => {
    const response = await api.get(`/api/cpd/tasks/${taskId}/questions`);
    return response.data;
  },

  completeVideo: async (taskId) => {
    const response = await api.post(`/api/cpd/tasks/${taskId}/complete-video`, { confirmed: true });
    return response.data;
  },

  completeReading: async (taskId) => {
    const response = await api.post(`/api/cpd/tasks/${taskId}/complete-reading`, { confirmed: true });
    return response.data;
  },

  submitQuiz: async (taskId, answers) => {
    const response = await api.post(`/api/cpd/tasks/${taskId}/submit-quiz`, { answers });
    return response.data;
  },

  downloadCertificate: async (taskId) => {
    const response = await api.get(`/api/cpd/tasks/${taskId}/certificate`, {
      responseType: 'blob',
    });
    return response;
  },

  downloadCaseStudy: async (taskId) => {
    const response = await api.get(`/api/cpd/tasks/${taskId}/case-study`, {
      responseType: 'blob',
    });
    return response;
  },

  getMyCompletions: async () => {
    const response = await api.get('/api/cpd/my-completions');
    return response.data;
  },

  getResourceInfo: async (taskId) => {
    const response = await api.get(`/api/cpd/tasks/${taskId}/resource-info`);
    return response.data;
  },

  getMySummary: async (year) => {
    const response = await api.get('/api/cpd/my-summary', { params: { year } });
    return response.data;
  },
};

// CPD Admin API calls
export const cpdAdminAPI = {
  getTasks: async () => {
    const response = await api.get('/api/cpd/admin/tasks');
    return response.data;
  },

  getNotificationConfig: async () => {
    const response = await api.get('/api/cpd/admin/notification-config');
    return response.data;
  },

  updateNotificationConfig: async (data) => {
    const response = await api.put('/api/cpd/admin/notification-config', data);
    return response.data;
  },

  createTask: async (data) => {
    const response = await api.post('/api/cpd/admin/tasks', data);
    return response.data;
  },

  updateTask: async (taskId, data) => {
    const response = await api.put(`/api/cpd/admin/tasks/${taskId}`, data);
    return response.data;
  },

  deleteTask: async (taskId) => {
    const response = await api.delete(`/api/cpd/admin/tasks/${taskId}`);
    return response.data;
  },

  uploadCertificate: async (taskId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`/api/cpd/admin/tasks/${taskId}/certificate`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  uploadCaseStudy: async (taskId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`/api/cpd/admin/tasks/${taskId}/case-study`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  getQuestions: async (taskId) => {
    const response = await api.get(`/api/cpd/admin/tasks/${taskId}/questions`);
    return response.data;
  },

  createQuestion: async (taskId, data) => {
    const response = await api.post(`/api/cpd/admin/tasks/${taskId}/questions`, data);
    return response.data;
  },

  updateQuestion: async (questionId, data) => {
    const response = await api.put(`/api/cpd/admin/questions/${questionId}`, data);
    return response.data;
  },

  deleteQuestion: async (questionId) => {
    const response = await api.delete(`/api/cpd/admin/questions/${questionId}`);
    return response.data;
  },

  getCompletions: async (taskId) => {
    const response = await api.get(`/api/cpd/admin/tasks/${taskId}/completions`);
    return response.data;
  },

  getComplianceReport: async (year) => {
    const response = await api.get('/api/cpd/admin/compliance-report', { params: { year } });
    return response.data;
  },

  exportComplianceCSV: async (year) => {
    const response = await api.get('/api/cpd/admin/compliance-report/export', {
      params: { year },
      responseType: 'blob',
    });
    return response;
  },
};

// SOA Builder API
export const soaBuilderAPI = {
  createDraft: async (data) => {
    const response = await api.post('/api/soa-builder/drafts', data);
    return response.data;
  },

  listDrafts: async (params = {}) => {
    const response = await api.get('/api/soa-builder/drafts', { params });
    return response.data;
  },

  getDraft: async (draftId) => {
    const response = await api.get(`/api/soa-builder/drafts/${draftId}`);
    return response.data;
  },

  updateDraft: async (draftId, data, { force = false } = {}) => {
    const response = await api.put(`/api/soa-builder/drafts/${draftId}`, data, {
      params: force ? { force: true } : {},
    });
    return response.data;
  },

  deleteDraft: async (draftId) => {
    const response = await api.delete(`/api/soa-builder/drafts/${draftId}`);
    return response.data;
  },

  finaliseDraft: async (draftId, { acknowledgeWarnings = false } = {}) => {
    const response = await api.post(`/api/soa-builder/drafts/${draftId}/finalise`, null, {
      params: acknowledgeWarnings ? { acknowledge_warnings: true } : {},
    });
    return response.data;
  },

  exportPdf: async (data) => {
    const response = await api.post('/api/soa-builder/export-pdf', data, {
      responseType: 'blob',
    });
    return response.data;
  },

  fetchAuditLog: async (draftId) => {
    const response = await api.get(`/api/soa-builder/drafts/${draftId}/audit-log`);
    return response.data;
  },

  checkCompliance: async (draftId) => {
    const response = await api.post(`/api/soa-builder/drafts/${draftId}/compliance-check`);
    return response.data;
  },

  getPortfolios: async () => {
    const response = await api.get('/api/soa-builder/portfolios');
    return response.data;
  },

  getFeeData: async (portfolioCode) => {
    const response = await api.get(`/api/soa-builder/fee-data/${portfolioCode}`);
    return response.data;
  },

  searchFunds: async (query) => {
    const response = await api.get('/api/soa-builder/search-funds', { params: { q: query } });
    return response.data;
  },

  calculateBlend: async (data) => {
    const response = await api.post('/api/soa-builder/calculate-blend', data);
    return response.data;
  },

  getAssetClasses: async () => {
    const response = await api.get('/api/soa-builder/asset-classes');
    return response.data;
  },

  calculateRetirement: async (params) => {
    const response = await api.post('/api/soa-builder/calculate-retirement', params);
    return response.data;
  },

  saveRetirementScenario: async (data) => {
    const response = await api.post('/api/soa-builder/save-retirement-scenario', data);
    return response.data;
  },

  // ── Duplicate & Move ──────────────────────────────────────────────
  duplicateDraft: async (draftId) => {
    const response = await api.post(`/api/soa-builder/drafts/${draftId}/duplicate`);
    return response.data;
  },

  moveDraft: async (draftId, folderId) => {
    const response = await api.patch(`/api/soa-builder/drafts/${draftId}/move`, { folder_id: folderId });
    return response.data;
  },

  // ── Folders ───────────────────────────────────────────────────────
  listFolders: async () => {
    const response = await api.get('/api/soa-builder/folders');
    return response.data;
  },

  createFolder: async (name, parentId = null) => {
    const response = await api.post('/api/soa-builder/folders', { name, parent_id: parentId });
    return response.data;
  },

  updateFolder: async (folderId, data) => {
    const response = await api.put(`/api/soa-builder/folders/${folderId}`, data);
    return response.data;
  },

  deleteFolder: async (folderId) => {
    const response = await api.delete(`/api/soa-builder/folders/${folderId}`);
    return response.data;
  },

  // ── Sharing ───────────────────────────────────────────────────────
  listDraftShares: async (draftId) => {
    const response = await api.get(`/api/soa-builder/drafts/${draftId}/shares`);
    return response.data;
  },

  shareDraft: async (draftId, userEmails) => {
    const response = await api.post(`/api/soa-builder/drafts/${draftId}/shares`, { user_emails: userEmails });
    return response.data;
  },

  unshareDraft: async (draftId, userEmail) => {
    const response = await api.delete(`/api/soa-builder/drafts/${draftId}/shares/${encodeURIComponent(userEmail)}`);
    return response.data;
  },

  listDomainUsers: async () => {
    const response = await api.get('/api/soa-builder/domain-users');
    return response.data;
  },

  // ── Adviser Profile ─────────────────────────────────────────────
  getAdviserProfile: async () => {
    const response = await api.get('/api/soa-builder/adviser-profile');
    return response.data;
  },

  saveAdviserProfile: async (data) => {
    const response = await api.put('/api/soa-builder/adviser-profile', data);
    return response.data;
  },

  // ── Client Registry ─────────────────────────────────────────────
  searchClients: async (search) => {
    const response = await api.get('/api/soa-builder/clients', { params: { search } });
    return response.data;
  },

  listClients: async () => {
    const response = await api.get('/api/soa-builder/clients');
    return response.data;
  },

  createClient: async (data) => {
    const response = await api.post('/api/soa-builder/clients', data);
    return response.data;
  },

  getClient: async (clientId) => {
    const response = await api.get(`/api/soa-builder/clients/${clientId}`);
    return response.data;
  },

  updateClient: async (clientId, data) => {
    const response = await api.put(`/api/soa-builder/clients/${clientId}`, data);
    return response.data;
  },

  deleteClient: async (clientId) => {
    const response = await api.delete(`/api/soa-builder/clients/${clientId}`);
    return response.data;
  },

  // ── SOA Templates ───────────────────────────────────────────────
  listTemplates: async (fieldKey) => {
    const params = fieldKey ? { field_key: fieldKey } : {};
    const response = await api.get('/api/soa-builder/templates', { params });
    return response.data;
  },

  createTemplate: async (data) => {
    const response = await api.post('/api/soa-builder/templates', data);
    return response.data;
  },

  updateTemplate: async (templateId, data) => {
    const response = await api.put(`/api/soa-builder/templates/${templateId}`, data);
    return response.data;
  },

  deleteTemplate: async (templateId) => {
    const response = await api.delete(`/api/soa-builder/templates/${templateId}`);
    return response.data;
  },

  setTemplateDefault: async (templateId) => {
    const response = await api.post(`/api/soa-builder/templates/${templateId}/set-default`);
    return response.data;
  },

  getTemplateDefaults: async () => {
    const response = await api.get('/api/soa-builder/templates/defaults');
    return response.data;
  },
};

export const tradingAPI = {
  // Portfolio Trading, Compliance & Execution (Drummond-internal)
  listSuites: async () => {
    const response = await api.get('/api/trading/suites');
    return response.data;
  },
  getSuiteMatrix: async (suite, saaYear) => {
    const q = saaYear ? `?saa_year=${saaYear}` : '';
    const response = await api.get(`/api/trading/suites/${encodeURIComponent(suite)}/matrix${q}`);
    return response.data;
  },
  getSuiteNeutrals: async (suite, saaYear) => {
    const q = saaYear ? `?saa_year=${saaYear}` : '';
    const response = await api.get(`/api/trading/suites/${encodeURIComponent(suite)}/neutrals${q}`);
    return response.data;
  },
  getPortfolioAllocation: async (portfolioCode) => {
    const response = await api.get(`/api/trading/portfolios/${encodeURIComponent(portfolioCode)}/allocation`);
    return response.data;
  },
  listLabPortfolios: async () => {
    const response = await api.get('/api/trading/lab-portfolios');
    return response.data;
  },
  listUnitClassMap: async () => {
    const response = await api.get('/api/trading/unit-class-map');
    return response.data;
  },
  createUnitClassMapping: async (body) => {
    const response = await api.post('/api/trading/unit-class-map', body);
    return response.data;
  },
  updateUnitClassMapping: async (id, body) => {
    const response = await api.put(`/api/trading/unit-class-map/${id}`, body);
    return response.data;
  },
  deleteUnitClassMapping: async (id) => {
    const response = await api.delete(`/api/trading/unit-class-map/${id}`);
    return response.data;
  },
  getLabPortfolioAllocation: async (id) => {
    const response = await api.get(`/api/trading/lab-portfolios/${id}/allocation`);
    return response.data;
  },
  importShadowTarget: async (portfolioCode) => {
    const response = await api.post(`/api/trading/portfolios/${encodeURIComponent(portfolioCode)}/import-shadow`);
    return response.data;
  },
  getRegistryInfo: async (blenderCode) => {
    const response = await api.get(`/api/trading/registry/${encodeURIComponent(blenderCode)}`);
    return response.data;
  },
  getAuditHistory: async () => {
    const response = await api.get('/api/trading/audit/history');
    return response.data;
  },
  getAuditEvolution: async (blenderCode) => {
    const response = await api.get(`/api/trading/audit/evolution/${encodeURIComponent(blenderCode)}`);
    return response.data;
  },
  getAuditCompare: async (a, b) => {
    const response = await api.get(`/api/trading/audit/compare?a=${a}&b=${b}`);
    return response.data;
  },
  getAuditBulk: async (blenderCodes) => {
    const response = await api.get(`/api/trading/audit/bulk?codes=${encodeURIComponent(blenderCodes.join(','))}`);
    return response.data;
  },
  getAuditVehicles: async (blenderCode) => {
    const response = await api.get(`/api/trading/audit/vehicles/${encodeURIComponent(blenderCode)}`);
    return response.data;
  },
  evaluateCompliance: async (suite, proposals, saaYear) => {
    const response = await api.post(`/api/trading/suites/${encodeURIComponent(suite)}/compliance`,
      { proposals, saa_year: saaYear || null });
    return response.data;
  },
  getMandateChecks: async (suite, basis, saaYear) => {
    const q = `?basis=${encodeURIComponent(basis || 'approved')}${saaYear ? `&saa_year=${saaYear}` : ''}`;
    const response = await api.get(`/api/trading/suites/${encodeURIComponent(suite)}/mandate-checks${q}`);
    return response.data;
  },
  setMandateAttestation: async (body) => {
    const response = await api.put('/api/trading/mandate-attestations', body);
    return response.data;
  },
  submitForApproval: async (suite, body) => {
    const response = await api.post(`/api/trading/suites/${encodeURIComponent(suite)}/submit`, body);
    return response.data;
  },
  discardDraft: async (setId) => {
    const response = await api.delete(`/api/trading/target-sets/${setId}`);
    return response.data;
  },
  listComplianceRules: async () => {
    const response = await api.get('/api/trading/compliance-rules');
    return response.data;
  },
  getComplianceCatalogue: async () => {
    const response = await api.get('/api/trading/compliance-catalogue');
    return response.data;
  },
  createComplianceRule: async (body) => {
    const response = await api.post('/api/trading/compliance-rules', body);
    return response.data;
  },
  updateComplianceRule: async (id, body) => {
    const response = await api.put(`/api/trading/compliance-rules/${id}`, body);
    return response.data;
  },
  deleteComplianceRule: async (id) => {
    const response = await api.delete(`/api/trading/compliance-rules/${id}`);
    return response.data;
  },
  listPendingApprovals: async () => {
    const response = await api.get('/api/trading/pending-approvals');
    return response.data;
  },
  getTargetSetCompliance: async (setId) => {
    const response = await api.get(`/api/trading/target-sets/${setId}/compliance`);
    return response.data;
  },
  approvalDecision: async (setId, decision, note) => {
    const response = await api.post(`/api/trading/target-sets/${setId}/decision`, { decision, note: note || '' });
    return response.data;
  },
  listStrategies: async () => {
    const response = await api.get('/api/trading/strategies');
    return response.data;
  },
  getVehicles: async (blenderCode) => {
    const response = await api.get(`/api/trading/strategies/${encodeURIComponent(blenderCode)}/vehicles`);
    return response.data;
  },
  getSeed: async (blenderCode) => {
    const response = await api.get(`/api/trading/strategies/${encodeURIComponent(blenderCode)}/seed`);
    return response.data;
  },
  getMatrix: async (blenderCode, portfolioCodes = []) => {
    const q = portfolioCodes.length ? `?portfolio_codes=${encodeURIComponent(portfolioCodes.join(','))}` : '';
    const response = await api.get(`/api/trading/strategies/${encodeURIComponent(blenderCode)}/matrix${q}`);
    return response.data;
  },
  listTargetSets: async (blenderCode) => {
    const response = await api.get(`/api/trading/strategies/${encodeURIComponent(blenderCode)}/target-sets`);
    return response.data;
  },
  getTargetSet: async (setId) => {
    const response = await api.get(`/api/trading/target-sets/${setId}`);
    return response.data;
  },
  saveDraft: async (blenderCode, payload) => {
    const response = await api.post(`/api/trading/strategies/${encodeURIComponent(blenderCode)}/draft`, payload);
    return response.data;
  },
  deleteOverride: async (overrideId) => {
    const response = await api.delete(`/api/trading/overrides/${overrideId}`);
    return response.data;
  },
  getCascade: async (blenderCode) => {
    const response = await api.get(`/api/trading/strategies/${encodeURIComponent(blenderCode)}/cascade`);
    return response.data;
  },
  validateSubs: async (blenderCode, lines) => {
    const response = await api.post(`/api/trading/strategies/${encodeURIComponent(blenderCode)}/validate-subs`, { lines });
    return response.data;
  },
  changesReport: async (blenderCodes, basis = 'approved', saaYear = null, approvedOffset = 1) => {
    const params = { blender_codes: blenderCodes.join(','), basis };
    if (saaYear) params.saa_year = saaYear;
    if (basis === 'approved' && approvedOffset > 1) params.approved_offset = approvedOffset;
    const response = await api.get('/api/trading/changes-report', { params });
    return response.data;
  },
  getBrandedPortfolios: async () => {
    const response = await api.get('/api/trading/branded-portfolios');
    return response.data;
  },
  restoreTargetSet: async (setId) => {
    const response = await api.post(`/api/trading/target-sets/${setId}/restore`);
    return response.data;
  },
  listFundAttributes: async () => {
    const response = await api.get('/api/trading/fund-attributes');
    return response.data;
  },
  exportSuiteGrid: async (suite, body) => {
    const response = await api.post(`/api/trading/suites/${encodeURIComponent(suite)}/export`, body, { responseType: 'blob' });
    return response.data;
  },
  listCustomInstruments: async () => {
    const response = await api.get('/api/trading/custom-instruments');
    return response.data;
  },
  createCustomInstrument: async (body) => {
    const response = await api.post('/api/trading/custom-instruments', body);
    return response.data;
  },
  updateCustomInstrument: async (id, body) => {
    const response = await api.put(`/api/trading/custom-instruments/${id}`, body);
    return response.data;
  },
  retireCustomInstrument: async (id) => {
    const response = await api.delete(`/api/trading/custom-instruments/${id}`);
    return response.data;
  },
  generateTradeSheets: async (blenderCodes, dryRun = false) => {
    const response = await api.post('/api/trading/trade-sheets/generate',
      { blender_codes: blenderCodes, dry_run: dryRun });
    return response.data;
  },
  downloadTradeSheets: async (runId) => {
    const response = await api.get(`/api/trading/trade-sheets/runs/${runId}/download`, { responseType: 'blob' });
    return response.data;
  },
  saveFundAttributes: async (items) => {
    const response = await api.put('/api/trading/fund-attributes', { items });
    return response.data;
  },
  downloadBulkTemplate: async () => {
    const response = await api.get('/api/trading/bulk-template', { responseType: 'blob' });
    return response.data;
  },
  bulkUploadTargets: async (file) => {
    const form = new FormData();
    form.append('file', file);
    const response = await api.post('/api/trading/bulk-upload', form,
      { headers: { 'Content-Type': 'multipart/form-data' } });
    return response.data;
  },
  getDivergence: async (blenderCode, portfolioCodes = []) => {
    const q = portfolioCodes.length ? `?portfolio_codes=${encodeURIComponent(portfolioCodes.join(','))}` : '';
    const response = await api.get(`/api/trading/strategies/${encodeURIComponent(blenderCode)}/divergence${q}`);
    return response.data;
  },
  getOverrides: async (blenderCode) => {
    const response = await api.get(`/api/trading/strategies/${encodeURIComponent(blenderCode)}/overrides`);
    return response.data;
  },
  saveOverrides: async (blenderCode, list) => {
    const response = await api.put(`/api/trading/strategies/${encodeURIComponent(blenderCode)}/overrides`, list);
    return response.data;
  },
  listUnitClassGroups: async () => {
    const response = await api.get('/api/trading/unit-class-groups');
    return response.data;
  },
  createUnitClassGroup: async (payload) => {
    const response = await api.post('/api/trading/unit-class-groups', payload);
    return response.data;
  },
};

export const projectsAPI = {
  // Projects
  listProjects: async (params = {}) => {
    const response = await api.get('/api/projects', { params });
    return response.data;
  },
  getProject: async (id) => {
    const response = await api.get(`/api/projects/${id}`);
    return response.data;
  },
  createProject: async (data) => {
    const response = await api.post('/api/projects', data);
    return response.data;
  },
  updateProject: async (id, data) => {
    const response = await api.put(`/api/projects/${id}`, data);
    return response.data;
  },
  archiveProject: async (id) => {
    const response = await api.delete(`/api/projects/${id}`);
    return response.data;
  },
  // Tasks
  createTask: async (projectId, data) => {
    const response = await api.post(`/api/projects/${projectId}/tasks`, data);
    return response.data;
  },
  updateTask: async (taskId, data) => {
    const response = await api.put(`/api/projects/tasks/${taskId}`, data);
    return response.data;
  },
  completeTask: async (taskId) => {
    const response = await api.post(`/api/projects/tasks/${taskId}/complete`);
    return response.data;
  },
  deleteTask: async (taskId) => {
    const response = await api.delete(`/api/projects/tasks/${taskId}`);
    return response.data;
  },
  getTaskDetail: async (taskId) => {
    const response = await api.get(`/api/projects/tasks/${taskId}/detail`);
    return response.data;
  },
  // Comments
  addComment: async (data) => {
    const response = await api.post('/api/projects/comments', data);
    return response.data;
  },
  deleteComment: async (id) => {
    const response = await api.delete(`/api/projects/comments/${id}`);
    return response.data;
  },
  // Resources
  addResourceLink: async (data) => {
    const response = await api.post('/api/projects/resources', data);
    return response.data;
  },
  uploadResource: async (file, fields = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    Object.entries(fields).forEach(([k, v]) => { if (v != null) formData.append(k, v); });
    const response = await api.post('/api/projects/resources/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  resourceDownloadUrl: async (id) => {
    const response = await api.get(`/api/projects/resources/${id}/download`);
    return response.data;
  },
  deleteResource: async (id) => {
    const response = await api.delete(`/api/projects/resources/${id}`);
    return response.data;
  },
  // Pickers / autocomplete
  listUsers: async () => {
    const response = await api.get('/api/projects/users');
    return response.data;
  },
  getSuggestions: async () => {
    const response = await api.get('/api/projects/suggestions');
    return response.data;
  },
};

export const portfolioLabAPI = {
  listPortfolios: async () => {
    const response = await api.get('/api/portfolio-lab/portfolios');
    return response.data;
  },

  getPortfolio: async (id) => {
    const response = await api.get(`/api/portfolio-lab/portfolios/${id}`);
    return response.data;
  },

  createPortfolio: async (data) => {
    const response = await api.post('/api/portfolio-lab/portfolios', data);
    return response.data;
  },

  updatePortfolio: async (id, data) => {
    const response = await api.put(`/api/portfolio-lab/portfolios/${id}`, data);
    return response.data;
  },

  deletePortfolio: async (id) => {
    const response = await api.delete(`/api/portfolio-lab/portfolios/${id}`);
    return response.data;
  },

  duplicatePortfolio: async (id) => {
    const response = await api.post(`/api/portfolio-lab/portfolios/${id}/duplicate`);
    return response.data;
  },

  assetClassLookup: async (trCodes) => {
    const response = await api.post('/api/portfolio-lab/asset-class-lookup', { tr_codes: trCodes });
    return response.data;
  },

  downloadBulkTemplate: async () => {
    const response = await api.get('/api/portfolio-lab/bulk-template', { responseType: 'blob' });
    return response;
  },

  bulkUploadPortfolios: async (file, commit = false) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`/api/portfolio-lab/bulk-upload?commit=${commit}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  getSAAPortfolios: async () => {
    const response = await api.get('/api/portfolio-lab/saa-portfolios');
    return response.data;
  },

  // { financial_years: [..desc], active_financial_year } for the vintage selector.
  getSAAVintages: async () => {
    const response = await api.get('/api/portfolio-lab/saa-vintages');
    return response.data;
  },

  // financialYear optional — selects a specific SAA vintage; null = live SAA.
  getSAAHoldings: async (codes, financialYear = null) => {
    const response = await api.post('/api/portfolio-lab/saa-holdings',
      { portfolio_codes: codes, financial_year: financialYear });
    return response.data;
  },

  getFactorExposures: async (portfolios) => {
    const response = await api.post('/api/portfolio-lab/factor-exposures', { portfolios });
    return response.data;
  },

  getPortfolioAnalytics: async (portfolios, benchmark_key = null, scenario_id = null) => {
    const payload = { portfolios };
    if (benchmark_key) payload.benchmark_key = benchmark_key;
    if (scenario_id) payload.scenario_id = scenario_id;
    const response = await api.post('/api/portfolio-lab/portfolio-analytics', payload);
    return response.data;
  },

  backtest: async (portfolios) => {
    const response = await api.post('/api/portfolio-lab/backtest', { portfolios });
    return response.data;
  },

  getActualPerformance: async (portfolio_codes, common_start_date) => {
    const response = await api.post('/api/portfolio-lab/actual-performance', {
      portfolio_codes,
      common_start_date,
    });
    return response.data;
  },
};

// Blender Registration API (admin only)
export const blenderRegistrationAPI = {
  registerPortfolio: async (data) => {
    const response = await api.post('/api/blender/register-portfolio', data);
    return response.data;
  },

  updatePortfolioWeights: async (code, data) => {
    const response = await api.put(`/api/blender/register-portfolio/${code}`, data);
    return response.data;
  },

  removePortfolio: async (code) => {
    const response = await api.delete(`/api/blender/register-portfolio/${code}`);
    return response.data;
  },

  listRegisteredPortfolios: async () => {
    const response = await api.get('/api/blender/registered-portfolios');
    return response.data;
  },
};

// Market Data saved chart configs (rendered server-side to house-style PNGs;
// consumed by the Chart_Deck PowerPoint builder)
export const marketDataAPI = {
  listChartConfigs: ({ favouritesOnly = false, mineOnly = false } = {}) => {
    const params = new URLSearchParams();
    if (favouritesOnly) params.set('favourites_only', 'true');
    if (mineOnly) params.set('mine_only', 'true');
    const q = params.toString() ? `?${params}` : '';
    return api.get(`/api/tactical/chart-configs${q}`).then(r => r.data);
  },
  getChartConfig: (id) =>
    api.get(`/api/tactical/chart-configs/${id}`).then(r => r.data),
  createChartConfig: (data) =>
    api.post('/api/tactical/chart-configs', data).then(r => r.data),
  updateChartConfig: (id, data) =>
    api.put(`/api/tactical/chart-configs/${id}`, data).then(r => r.data),
  deleteChartConfig: (id) =>
    api.delete(`/api/tactical/chart-configs/${id}`).then(r => r.data),
};

export const attributionAPI = {
  getPortfolios: () =>
    api.get('/api/attribution/portfolios').then(r => r.data),
  getQuarters: (code) =>
    api.get(`/api/attribution/quarters/${code}`).then(r => r.data),
  getPeriods: (code, reportType = 'QUARTERLY') =>
    api.get(`/api/attribution/periods/${code}`, { params: { report_type: reportType } }).then(r => r.data),
  getCumulativeReturns: (code, params = {}) =>
    api.get(`/api/attribution/cumulative-returns/${code}`, { params }).then(r => r.data),
  getMonthlyReturns: (code, params = {}) =>
    api.get(`/api/attribution/monthly-returns/${code}`, { params }).then(r => r.data),
  getKeyStats: (code, params = {}) =>
    api.get(`/api/attribution/key-stats/${code}`, { params }).then(r => r.data),
  getBrinsonSAC: (code, params = {}) =>
    api.get(`/api/attribution/brinson-sac/${code}`, { params }).then(r => r.data),
  getBrinsonFunds: (code, params = {}) =>
    api.get(`/api/attribution/brinson-funds/${code}`, { params }).then(r => r.data),
  getBrinsonPortfolioTotal: (code, params = {}) =>
    api.get(`/api/attribution/brinson-portfolio-total/${code}`, { params }).then(r => r.data),
  // RANGE-mode endpoints — accept start_date + end_date
  getRangeReportInfo: (code, params = {}) =>
    api.get(`/api/attribution/range/report-info/${code}`, { params }).then(r => r.data),
  getRangeKeyStats: (code, params = {}) =>
    api.get(`/api/attribution/range/key-stats/${code}`, { params }).then(r => r.data),
  getRangeBrinsonPortfolioTotal: (code, params = {}) =>
    api.get(`/api/attribution/range/brinson-portfolio-total/${code}`, { params }).then(r => r.data),
  getRangeBrinsonSAC: (code, params = {}) =>
    api.get(`/api/attribution/range/brinson-sac/${code}`, { params }).then(r => r.data),
  getRangeBrinsonFunds: (code, params = {}) =>
    api.get(`/api/attribution/range/brinson-funds/${code}`, { params }).then(r => r.data),
  getRangeContributions: (code, params = {}) =>
    api.get(`/api/attribution/range/contributions/${code}`, { params }).then(r => r.data),
  getRangeBrinsonTimeSeries: (code, params = {}) =>
    api.get(`/api/attribution/range/brinson-time-series/${code}`, { params }).then(r => r.data),
  getContributions: (code, params = {}) =>
    api.get(`/api/attribution/contributions/${code}`, { params }).then(r => r.data),
  getCharacteristics: (code, params = {}) =>
    api.get(`/api/attribution/characteristics/${code}`, { params }).then(r => r.data),
  getSectorHistory: (code, params = {}) =>
    api.get(`/api/attribution/sector-history/${code}`, { params }).then(r => r.data),
  getReport: (code, params = {}) =>
    api.get(`/api/attribution/report/${code}`, { params }).then(r => r.data),
  getAnnualisedReturn: (code, params = {}) =>
    api.get(`/api/attribution/annualised-return/${code}`, { params }).then(r => r.data),
  getBrinsonTimeSeries: (code, params = {}) =>
    api.get(`/api/attribution/brinson-time-series/${code}`, { params }).then(r => r.data),
};
